import React from "react";
import { Link } from "react-router-dom";
import EmployeePageShell from "./EmployeePageShell";
import useFetch from "../../useFetch";
import { getStoredUser } from "../../auth";
import EmptyState from "../Common/EmptyState";
import AccountReferralCard from "../Common/AccountReferralCard";
import { getSafeExternalUrl } from "../../utils/externalUrls";

const infoItems = (profile) => [
  { id: "industry", label: "Industry", value: profile.industry },
  { id: "size", label: "Company size", value: profile.company_size },
  { id: "founded", label: "Founded", value: profile.founded_year },
  { id: "location", label: "Location", value: profile.location },
  { id: "email", label: "Email", value: profile.email },
  { id: "phone", label: "Phone", value: profile.phone },
].filter((item) => item.value);

const EmployeeViewCompanyProfile = () => {
  const storedUser = getStoredUser();
  const { response, isLoading, error } = useFetch(process.env.REACT_APP_BASE_API_URL + "/company-profile");
  const profile = response?.company_profile;
  const account = response?.account ?? storedUser;
  const safeWebsite = getSafeExternalUrl(profile?.website);

  return (
    <EmployeePageShell
      active="profile-view"
      title="View Company Profile"
      text="Preview how candidates will understand your company."
    >
      <div className="ls-widget employee-company-preview-widget">
        <div className="widget-title">
          <h4>Company Preview</h4>
          <Link to="/employee-company-profile" className="theme-btn btn-style-three">Edit Profile</Link>
        </div>
        <div className="widget-content">
          {isLoading && <h5 className="text-center my-3">Loading Data</h5>}
          {error && <div className="alert alert-danger"><strong>{error}</strong></div>}
          {!isLoading && !error && (
            <AccountReferralCard account={account} className="profile-account-referral" />
          )}
          {!isLoading && !error && !profile && (
            <EmptyState
              icon="la la-building"
              title="No company profile yet"
              text="Add your business details, logo, and overview so candidates can understand your company."
              actionText="Add Company Profile"
              actionTo="/employee-company-profile"
            />
          )}

          {profile && (
            <div className="employee-company-preview">
              <div className="employee-company-cover">
                {profile.cover_image_url && (
                  <img src={profile.cover_image_url} alt={`${profile.company_name} cover`} />
                )}
              </div>
              <div className="employee-company-preview-head">
                <div className="employee-company-logo">
                  {profile.logo_url ? (
                    <img src={profile.logo_url} alt={profile.company_name} />
                  ) : (
                    <span>{profile.company_name?.charAt(0) ?? "E"}</span>
                  )}
                </div>
                <div>
                  <h4>{profile.company_name}</h4>
                  <p>{[profile.industry, profile.location].filter(Boolean).join(" - ")}</p>
                </div>
              </div>

              <div className="employee-company-preview-grid">
                {infoItems(profile).map((item) => (
                  <div key={item.id}>
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>

              {profile.overview && (
                <div className="employee-company-preview-section">
                  <h4>Company Overview</h4>
                  <p>{profile.overview}</p>
                </div>
              )}

              {profile.culture && (
                <div className="employee-company-preview-section">
                  <h4>Culture & Benefits</h4>
                  <p>{profile.culture}</p>
                </div>
              )}

              {safeWebsite && (
                <a href={safeWebsite} target="_blank" rel="noreferrer" className="theme-btn btn-style-one">
                  Visit Website
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </EmployeePageShell>
  );
};

export default EmployeeViewCompanyProfile;
